import { useState, useCallback, useRef } from 'react';
import { Maximize, Upload, Download, Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const PRESETS = [
  { label: '1920×1080', w: 1920, h: 1080 },
  { label: '1280×720', w: 1280, h: 720 },
  { label: '800×600', w: 800, h: 600 },
  { label: '1:1 (512)', w: 512, h: 512 },
  { label: '4:3 (1024)', w: 1024, h: 768 },
];

const ImageResizer = () => {
  useToolUsage('image-resizer');
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [origW, setOrigW] = useState(0);
  const [origH, setOrigH] = useState(0);
  const [keepAspect, setKeepAspect] = useState(true);

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    setFile(f);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setOrigW(img.width);
      setOrigH(img.height);
      setWidth(img.width);
      setHeight(img.height);
      setPreview(URL.createObjectURL(f));
    };
    img.src = URL.createObjectURL(f);
  }, []);

  const updateWidth = (w: number) => {
    setWidth(w);
    if (keepAspect && origW) setHeight(Math.round((w / origW) * origH));
  };

  const updateHeight = (h: number) => {
    setHeight(h);
    if (keepAspect && origH) setWidth(Math.round((h / origH) * origW));
  };

  const applyPreset = (w: number, h: number) => {
    setWidth(w);
    setHeight(h);
  };

  const handleDownload = () => {
    if (!image) return;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(image, 0, 0, width, height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `resized_${file?.name || 'image.png'}`;
      a.click();
      toast.success('Downloaded!');
    }, 'image/png');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Maximize className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Image Resizer</h1>
          <p className="text-muted-foreground">Resize images to any dimension</p>
        </div>
      </div>

      {!image ? (
        <Card>
          <CardContent className="pt-6">
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary transition-colors">
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Drop an image here or click to upload</p>
              </div>
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </label>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                <img src={preview} alt="Preview" className="max-w-full max-h-full object-contain" />
              </div>
              <p className="text-sm text-muted-foreground text-center">Original: {origW} × {origH}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                {keepAspect ? <Lock className="h-4 w-4 text-primary" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
                <Label>Maintain aspect ratio</Label>
                <Switch checked={keepAspect} onCheckedChange={setKeepAspect} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Width</Label>
                  <Input type="number" value={width} onChange={e => updateWidth(Number(e.target.value))} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Height</Label>
                  <Input type="number" value={height} onChange={e => updateHeight(Number(e.target.value))} />
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map(p => (
                    <Button key={p.label} size="sm" variant="outline" onClick={() => applyPreset(p.w, p.h)}>{p.label}</Button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleDownload} className="flex-1">
                  <Download className="h-4 w-4 mr-2" /> Download ({width}×{height})
                </Button>
                <Button variant="outline" onClick={() => { setImage(null); setFile(null); setPreview(''); }}>Clear</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ImageResizer;
